import type { AssistRoute } from './assist.types'

/**
 * Both routes, in the order the contract puts them on the wire and the order
 * the buttons appear in.
 */
export const ASSIST_ROUTES: readonly AssistRoute[] = ['websearch', 'backlog']

/** Everything on, which is what the server does with no `assistRoutes`. */
export const DEFAULT_ASSIST_ROUTES: ReadonlySet<AssistRoute> = new Set(ASSIST_ROUTES)

/**
 * @param routes The reader's choice.
 * @returns The array `config.assistRoutes` and `assist.routes.update` take,
 *   always websearch before backlog.
 */
export function assistRoutesToWire(routes: ReadonlySet<AssistRoute>): AssistRoute[] {
  return ASSIST_ROUTES.filter((route) => routes.has(route))
}

/**
 * Read a stored or received list of routes.
 *
 * @param value Anything out of persisted settings or off a socket frame.
 * @returns The routes, or null when none of the names were recognised —
 *   which the contract reads the same as sending nothing: both routes.
 */
export function parseAssistRoutes(value: unknown): ReadonlySet<AssistRoute> | null {
  if (!Array.isArray(value)) return null
  const routes = value.filter(
    (route): route is AssistRoute => route === 'websearch' || route === 'backlog'
  )
  return routes.length > 0 ? new Set(routes) : null
}

export interface AssistRoutesStatus {
  /** The selection the server applied. */
  selected: ReadonlySet<AssistRoute>
  /** False only when the reader cannot fix the Backlog side themselves. */
  backlogAvailable: boolean
  /** Why, when it is false. Null otherwise. */
  backlogReason: string | null
}

/**
 * Read an `assist.routes.status` frame.
 *
 * @param event The frame as it arrived.
 * @returns The status, or null when the frame is not an object at all.
 */
export function parseAssistRoutesStatus(event: unknown): AssistRoutesStatus | null {
  if (typeof event !== 'object' || event === null) return null
  const record = event as Record<string, unknown>
  const reason = typeof record.backlogReason === 'string' ? record.backlogReason.trim() : ''

  return {
    selected: parseAssistRoutes(record.selected) ?? DEFAULT_ASSIST_ROUTES,
    // Absent reads as available: a server that predates the field is not
    // telling us Backlog is broken, and dimming the button on its silence
    // would take away a route that works.
    backlogAvailable: record.backlogAvailable !== false,
    backlogReason: reason === '' ? null : reason,
  }
}
